import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Route, Switch, Redirect, withRouter, useHistory } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import AppLayout from 'layouts/app-layout';
import SettingsLayout from 'layouts/settings-layout';
import OnboardingLayout from 'layouts/onboarding-layout';
import AuthLayout from 'layouts/auth-layout';
import AppLocale from 'lang';
import { IntlProvider } from 'react-intl';
import { ConfigProvider } from 'antd';
import useBodyClass from 'hooks/useBodyClass';
import Loading from 'components/shared-components/Loading'
import { AppViews } from './app-views';
import AuthViews from './auth-views/index'
import Utils from 'utils';
import { account, user } from 'api';
import { saveAccount } from 'redux/actions/Account';
import { saveUser, saveUserAccounts } from 'redux/actions/User';
import { setLoading } from 'redux/actions/Loader';
import useThemeDetector from 'hooks/useThemeDetector';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import { onSwitchTheme } from 'redux/actions';

export const Views = (props) => {
  const { locale, location, direction } = props;
  const currentAppLocale = AppLocale[locale];
  const [appLoading, setAppLoading] = useState(true);
  const dispatch = useDispatch();
  const history = useHistory();
  const systemTheme = useThemeDetector();
  const { switcher, themes } = useThemeSwitcher();
  const currentUserId = useSelector(state => state.user?.details?.id);
  const currentUser = useSelector(state => state.user?.details);
  const currentAccount = useSelector(state => state.account?.details);


  useBodyClass(`dir-${direction}`);

  useEffect(async () => {
    toggleTheme(systemTheme);
    if (Utils.getSessionToken() && !currentUserId) {
      const userDetails = await user.details();
      dispatch(saveUser(userDetails.user));
      const userAccounts = await user.accounts();
      dispatch(saveUserAccounts(userAccounts.accounts));
      const accountDetails = await account.details();
      dispatch(saveAccount(accountDetails.account));
      setAppLoading(false);
    } else {
      setAppLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser || currentUser?.appearance === 'system') {
      toggleTheme(systemTheme);
    } else {
      toggleTheme(currentUser?.appearance === 'dark');
    }
  }, [currentUser])



  const toggleTheme = (isTrue) => {
    const changedTheme = isTrue ? 'dark' : 'light'; 
    dispatch(onSwitchTheme(changedTheme));
    switcher({ theme: themes[changedTheme] });
  };

  if (appLoading) {
    return <Loading default />;
  }


  const renderAppRoutes = () => {
    if (currentUserId) {
      return !currentUser.confirmed_email ?
        renderUnconfirmedRoutes() :
        renderAppConditionalRoutes()
    };
  }

  const renderUnconfirmedRoutes = () => { 

    return (
      <AuthLayout direction={direction}>
        <Suspense fallback={<Loading cover="page" fallback={true} />}>
          <Redirect from=" " to={`/confirm${props.location.search}`} />
          <Route path={'/confirm'} exact component={lazy(() => import('./auth-views/confirm'))} />
        </Suspense>
      </AuthLayout>
    )
  }

  const renderAppConditionalRoutes = () => {
    const path = history.location?.pathname?.split('/');
    const isSettingsPath = path[1] === 'settings';
    
    if (!currentAccount?.onboarding_complete) {
      return <OnboardingLayout direction={direction} location={location} />
    }
    else {
      return (
        <>
          {isSettingsPath ?
            <SettingsLayout direction={direction} location={location} /> :
            <AppLayout direction={direction} location={location} />}
        </>
      )
    }
  }

  return (
    <IntlProvider
      locale={currentAppLocale.locale}
      messages={currentAppLocale.messages}>
      <ConfigProvider locale={currentAppLocale.antd} direction={direction}>
        <Switch>
          {renderAppRoutes()}
          <AuthLayout direction={direction} />
        </Switch>
        <Loading />
      </ConfigProvider>
    </IntlProvider>
  )
}

const mapStateToProps = ({ theme }) => {
  const { locale, direction } = theme;
  return { locale, direction }
};

export default withRouter(connect(mapStateToProps)(Views));